import React from 'react';
import { HiCheck } from 'react-icons/hi';

import { DefectCatalogItem } from 'domain/surveyDataManager/contracts';
import type { ActiveDefectLayerId } from 'state/surveyDataManager/activeLayersSlice';

import { humanizeSlug } from '../../../utils/formatters';
import {
  deriveDefectsForSurveyType,
  isWizardDefectGroupFullyActive,
  WizardCatalogSearchMatch,
} from '../wizardLogic';

export interface WizardStepSurveyTypeProps {
  readonly surveyTypes: readonly string[];
  readonly filteredSurveyTypes: readonly string[];
  readonly catalogMatches: readonly WizardCatalogSearchMatch[];
  readonly defects: readonly DefectCatalogItem[];
  readonly activeLayerIds: ReadonlySet<ActiveDefectLayerId>;
  readonly selectedSurveyType: string | null;
  readonly selectedDefectGroup: string | null;
  readonly surveyTypeSearch: string;
  readonly isLoading: boolean;
  readonly onSearchChange: (value: string) => void;
  readonly onSelectCatalogMatch: (match: WizardCatalogSearchMatch) => void;
  readonly onSelectSurveyType: (surveyType: string) => void;
}

function WizardStepSurveyType({
  surveyTypes,
  filteredSurveyTypes,
  catalogMatches,
  defects,
  activeLayerIds,
  selectedSurveyType,
  selectedDefectGroup,
  surveyTypeSearch,
  isLoading,
  onSearchChange,
  onSelectCatalogMatch,
  onSelectSurveyType,
}: WizardStepSurveyTypeProps): JSX.Element {
  const hasSearchQuery = surveyTypeSearch.trim().length > 0;
  const showSurveyTypeList = !hasSearchQuery || catalogMatches.length === 0;

  return (
    <div className="sdm-add-layer-wizard__panel">
      <h4 className="sdm-add-layer-wizard__panel-title">Find survey data</h4>
      <input
        type="search"
        className="sdm-add-layer-wizard__search"
        placeholder="Search e.g. Scanner cracking…"
        value={surveyTypeSearch}
        onChange={(event) => onSearchChange(event.target.value)}
        aria-label="Search survey type and parameter group"
      />
      {isLoading ? (
        <p className="sdm-add-layer-wizard__hint">Loading catalogue…</p>
      ) : null}

      {!isLoading && hasSearchQuery && catalogMatches.length > 0 ? (
        <div className="sdm-add-layer-wizard__search-section">
          <p className="sdm-add-layer-wizard__search-section-title">Matching layers</p>
          <ul className="sdm-add-layer-wizard__list">
            {catalogMatches.map((match) => {
              const isSelected =
                selectedSurveyType === match.surveyType &&
                selectedDefectGroup === match.defect.type;
              const isDisabled = isWizardDefectGroupFullyActive(
                activeLayerIds,
                defects,
                match.surveyType,
                match.defect.type,
              );

              return (
                <li key={`${match.surveyType}:${match.defect.slug}`}>
                  <button
                    type="button"
                    className={`sdm-add-layer-wizard__list-item${
                      isSelected ? ' sdm-add-layer-wizard__list-item--selected' : ''
                    }${isDisabled ? ' sdm-add-layer-wizard__list-item--disabled' : ''}`}
                    onClick={() => onSelectCatalogMatch(match)}
                    disabled={isDisabled}
                    aria-disabled={isDisabled || undefined}
                    aria-pressed={isSelected}
                  >
                    <span>
                      <strong>
                        {match.label}
                        {isDisabled ? ' (already active)' : ''}
                      </strong>
                      <span className="sdm-add-layer-wizard__list-meta">
                        Survey type: {humanizeSlug(match.surveyType)} · Parameter group:{' '}
                        {match.defect.type}
                      </span>
                    </span>
                    {isSelected ? <HiCheck /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {!isLoading && hasSearchQuery && catalogMatches.length === 0 ? (
        <p className="sdm-add-layer-wizard__hint" role="status">
          No matching survey type and parameter group. Try another search or pick a survey type below.
        </p>
      ) : null}

      {!isLoading && showSurveyTypeList ? (
        <>
          <p className="sdm-add-layer-wizard__search-section-title">
            {hasSearchQuery ? 'Survey types' : 'Or select survey type'}
          </p>
          <ul className="sdm-add-layer-wizard__list">
            {filteredSurveyTypes.length === 0 ? (
              <li>
                <p className="sdm-add-layer-wizard__hint" role="status">
                  No survey types are available in the catalogue.
                </p>
              </li>
            ) : null}
            {filteredSurveyTypes.map((type) => {
              const count = deriveDefectsForSurveyType(defects, type).length;
              const isSelected =
                selectedSurveyType === type && selectedDefectGroup === null;

              return (
                <li key={type}>
                  <button
                    type="button"
                    className={`sdm-add-layer-wizard__list-item${
                      isSelected ? ' sdm-add-layer-wizard__list-item--selected' : ''
                    }`}
                    onClick={() => onSelectSurveyType(type)}
                    aria-pressed={isSelected}
                  >
                    <span>
                      <strong>{humanizeSlug(type)}</strong>
                      <span className="sdm-add-layer-wizard__list-meta">
                        {count} catalogue entr{count === 1 ? 'y' : 'ies'}
                      </span>
                    </span>
                    {isSelected ? <HiCheck /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="sdm-add-layer-wizard__hint">
            Showing {filteredSurveyTypes.length} of {surveyTypes.length} survey types
          </p>
        </>
      ) : null}
    </div>
  );
}

export default WizardStepSurveyType;
